/**
 * Audio Manager
 * Handles all audio-related functionality for WebRTC connections
 */

/**
 * Audio stream configuration
 */
interface AudioConfig {
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
  sampleRate?: number;
  channelCount?: number;
}

/**
 * Audio Manager class
 * Separated from WebRTCConnection for better organization and testability
 */
export class AudioManager {
  private localAudioStream: MediaStream | null = null;
  private isLocalAudioEnabled: boolean = true;
  private isTwoWayActive: boolean = false;
  private audioConfig: AudioConfig;
  private audioTrackMonitor: NodeJS.Timeout | null = null;

  constructor(audioConfig: AudioConfig = {}) {
    this.audioConfig = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      ...audioConfig
    };
  }

  /**
   * Start two-way audio communication
   */
  async startTwoWayAudio(peerConnection: RTCPeerConnection): Promise<void> {
    if (this.isTwoWayActive) {
      return;
    }

    if (!peerConnection) {
      throw new Error('Peer connection is required for two-way audio');
    }

    try {
      // Get local audio stream (microphone)
      this.localAudioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: this.audioConfig.echoCancellation,
          noiseSuppression: this.audioConfig.noiseSuppression,
          autoGainControl: this.audioConfig.autoGainControl,
          sampleRate: this.audioConfig.sampleRate,
          channelCount: this.audioConfig.channelCount
        },
        video: false
      });


      // Add local audio tracks to peer connection
      this.localAudioStream.getAudioTracks().forEach(track => {
        peerConnection.addTrack(track, this.localAudioStream!);
        
        // Monitor track state changes
        track.onended = () => {
        };
        
        track.onmute = () => {
        };
        
        track.onunmute = () => {
        };
      });

      // Trigger renegotiation for audio
      await this.renegotiateForAudio(peerConnection);

      this.isTwoWayActive = true;
      
      // Start monitoring actual audio transmission
      this.startAudioTransmissionMonitoring(peerConnection);

    } catch (error) {
      
      // Cleanup on error
      if (this.localAudioStream) {
        this.localAudioStream.getTracks().forEach(track => track.stop());
        this.localAudioStream = null;
      }
      
      throw new Error(`Failed to start two-way audio: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Stop two-way audio communication
   */
  async stopTwoWayAudio(peerConnection?: RTCPeerConnection): Promise<void> {
    if (!this.isTwoWayActive) {
      return;
    }

    try {
      // Stop local audio stream
      if (this.localAudioStream) {
        this.localAudioStream.getTracks().forEach(track => {
          track.stop();
        });
        this.localAudioStream = null;
      }
      
      // Stop audio transmission monitoring
      this.stopAudioTransmissionMonitoring();

      // Remove audio tracks from peer connection
      if (peerConnection) {
        const senders = peerConnection.getSenders();
        const audioSenders = senders.filter(sender => 
          sender.track && sender.track.kind === 'audio'
        );

        for (const sender of audioSenders) {
          await peerConnection.removeTrack(sender);
        }
      }

      this.isTwoWayActive = false;

    } catch (error) {
      // Don't throw error on stop, just log it
    } finally {
      this.isTwoWayActive = false;
    }
  }

  /**
   * Enable/disable local audio (microphone mute/unmute)
   */
  setLocalAudioEnabled(enabled: boolean): void {
    if (!this.localAudioStream) {
      return;
    }

    this.localAudioStream.getAudioTracks().forEach(track => {
      track.enabled = enabled;
    });

    this.isLocalAudioEnabled = enabled;
  }

  /**
   * Get local audio stream
   */
  getLocalAudioStream(): MediaStream | null {
    return this.localAudioStream;
  }

  /**
   * Check if two-way audio is active
   */
  isTwoWayAudioActive(): boolean {
    return this.isTwoWayActive;
  }

  /**
   * Check if local audio is enabled
   */
  isLocalAudioMuted(): boolean {
    return !this.isLocalAudioEnabled;
  }

  /**
   * Update audio configuration
   */
  updateConfig(newConfig: Partial<AudioConfig>): void {
    this.audioConfig = { ...this.audioConfig, ...newConfig };
  }

  /**
   * Clean up all audio resources
   */
  cleanup(): void {
    // Stop monitoring
    this.stopAudioTransmissionMonitoring();

    if (this.localAudioStream) {
      this.localAudioStream.getTracks().forEach(track => track.stop());
      this.localAudioStream = null;
    }

    this.isTwoWayActive = false;
    this.isLocalAudioEnabled = true;
  }

  /**
   * Renegotiate peer connection for audio
   */
  private async renegotiateForAudio(peerConnection: RTCPeerConnection): Promise<void> {
    if (!peerConnection) {
      throw new Error('Peer connection not available for renegotiation');
    }

    try {
      // Create new offer with audio
      const offer = await peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });

      // Set local description
      await peerConnection.setLocalDescription(offer);
      
      // Mark audio renegotiation in progress in parent connection
      this.markAudioRenegotiationInProgress(peerConnection);

    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Start monitoring actual audio transmission
   */
  private startAudioTransmissionMonitoring(peerConnection: RTCPeerConnection): void {
    if (this.audioTrackMonitor) {
      clearInterval(this.audioTrackMonitor);
    }
    
    this.audioTrackMonitor = setInterval(async () => {
      if (peerConnection && peerConnection.connectionState === 'connected') {
        try {
          const stats = await peerConnection.getStats();
          
          stats.forEach((report) => {
            // Monitor outbound audio RTP stats (local audio transmission)
            if (report.type === 'outbound-rtp' && report.mediaType === 'audio') {
              // Check if actually transmitting data
              if (report.bytesSent > 0) {
              } else {
              }
            }
            
            // Monitor inbound audio RTP stats (remote audio reception)
            if (report.type === 'inbound-rtp' && report.mediaType === 'audio') {
            }
            
            // Monitor ICE candidate pair for connection quality
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            }
          });
          
        } catch (error) {
        }
      }
    }, 2000); // Check every 2 seconds
  }
  
  /**
   * Stop audio transmission monitoring
   */
  private stopAudioTransmissionMonitoring(): void {
    if (this.audioTrackMonitor) {
      clearInterval(this.audioTrackMonitor);
      this.audioTrackMonitor = null;
    }
  }
  
  /**
   * Mark audio renegotiation in progress (communicates with WebRTCConnection)
   */
  private markAudioRenegotiationInProgress(peerConnection: RTCPeerConnection): void {
    // Store renegotiation flag in a way WebRTCConnection can access
    (peerConnection as any).__audioRenegotiationInProgress = true;
    (peerConnection as any).__lastOfferCreationTime = Date.now();
  }
}